import React, { useEffect, useMemo, useState } from 'react';
import './FilterBar.css';
import TeamBadge from './TeamBadge';
import Tooltip from './Tooltip';
import { RoleChip } from './auction/auctionCommon';
import {
  Search,
  FilterX,
  Shield,
  Users,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'all', label: 'Tutti',  short: 'ALL' },
  { value: 'P',   label: 'Portieri',     short: 'P' },
  { value: 'D',   label: 'Difensori',    short: 'D' },
  { value: 'C',   label: 'Centrocampisti', short: 'C' },
  { value: 'A',   label: 'Attaccanti',   short: 'A' },
];

const SORT_OPTIONS = [
  { value: 'value_desc',         label: 'Quota',        hint: 'Quotazione' },
  { value: 'caps_desc',          label: 'Partite',      hint: 'Partite giocate' },
  { value: 'markavg_desc',       label: 'MV',           hint: 'Media voto' },
  { value: 'fmarkavg_desc',      label: 'MFV',          hint: 'Media fantavoto' },
  { value: 'goals_desc',         label: 'Gol fatti',    hint: 'Gol fatti' },
  { value: 'goals_against_desc', label: 'Gol subiti',   hint: 'Gol subiti' },
  { value: 'assists_desc',       label: 'Assist',       hint: 'Assist' },
  { value: 'ycards_desc',        label: 'Ammonizioni',  hint: 'Ammonizioni (cartellini gialli)' },
  { value: 'rcards_desc',        label: 'Espulsioni',   hint: 'Espulsioni (cartellini rossi)' },
  { value: 'owngoals_desc',      label: 'Autogol',      hint: 'Autogol' },
  { value: 'saves_desc',         label: 'Rig. parati',  hint: 'Rigori parati' },
  { value: 'mpenalties_desc',    label: 'Rig. sbag.',   hint: 'Rigori sbagliati' },
  { value: 'last5_desc',         label: 'Ultime 5',     hint: 'Media delle ultime 5 giornate' },
];

function normalizeTeamName(team) {
  return String(team || '').trim();
}

function buildTeams(players = []) {
  const set = new Set();
  (players || []).forEach((player) => {
    const team = normalizeTeamName(player?.team);
    if (team) set.add(team);
  });
  return Array.from(set).sort((a, b) => a.localeCompare(b, 'it'));
}

const ROLE_COLOR = { P: 'amber', D: 'green', C: 'blue', A: 'red' };

export default function FilterBar({
  players = [],
  search = '',
  setSearch,
  sort = 'value_desc',
  setSort,
  role = 'all',
  setRole,
  team = 'all',
  setTeam,
  onClear,
}) {
  const teams = useMemo(() => buildTeams(players), [players]);
  const [teamsExpanded, setTeamsExpanded] = useState(() => window.innerWidth >= 768);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const TEAMS_COLLAPSED_COUNT = 11;
  const visibleTeams = teamsExpanded ? teams : teams.slice(0, TEAMS_COLLAPSED_COUNT);
  const hasMoreTeams = teams.length > TEAMS_COLLAPSED_COUNT;

  useEffect(() => {
    if (team && team !== 'all' && !teams.includes(team)) {
      setTeam?.('all');
    }
  }, [team, teams, setTeam]);

  const activeCount = [
    search.trim() !== '',
    role !== 'all',
    team !== 'all',
    sort !== 'value_desc',
  ].filter(Boolean).length;

  const handleClear = () => {
    setSearch?.('');
    setRole?.('all');
    setTeam?.('all');
    setSort?.('value_desc');
    onClear?.();
  };

  return (
    <section className="fb">
      {/* ── Top row: search + clear ── */}
      <div className="fb__top">
        <div className="fb__search">
          <Search size={15} className="fb__search-icon" aria-hidden="true" />
          <input
            type="search"
            placeholder="Cerca giocatore, squadra o ruolo..."
            value={search}
            onChange={(e) => setSearch?.(e.target.value)}
            aria-label="Cerca giocatore"
          />
          {search && (
            <button
              className="fb__search-clear"
              onClick={() => setSearch?.('')}
              aria-label="Cancella ricerca"
            >
              ×
            </button>
          )}
        </div>

        <button
          type="button"
          className={`fb__reset ${activeCount > 0 ? 'fb__reset--active' : ''}`}
          onClick={handleClear}
          aria-label="Reset filtri"
        >
          <FilterX size={14} />
          <span>Reset</span>
          {activeCount > 0 && (
            <span className="fb__reset-badge">{activeCount}</span>
          )}
        </button>
      </div>

      {/* ── Mobile toggle: mostra/nasconde filtri e ordinamento ── */}
      <button
        type="button"
        className="fb__mobileToggle"
        onClick={() => setMobileFiltersOpen((v) => !v)}
        aria-expanded={mobileFiltersOpen}
      >
        <span className="fb__mobileToggle-label">
          <SlidersHorizontal size={14} />
          Filtri e ordinamento
          {activeCount > 0 && <span className="fb__reset-badge">{activeCount}</span>}
        </span>
        {mobileFiltersOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>

      <div className={`fb__collapsible ${mobileFiltersOpen ? 'is-open' : ''}`}>

      {/* ── Filters row ── */}
      <div className="fb__filters">

        {/* RUOLO */}
        <div className="fb__panel fb__panel--role">
          <div className="fb__panel-head">
            <Shield size={13} aria-hidden="true" />
            <span>Ruolo</span>
          </div>
          <div className="fb__role-row" role="group" aria-label="Filtro ruolo">
            {ROLE_OPTIONS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`fb__role-btn fb__role-btn--${item.value === 'all' ? 'all' : ROLE_COLOR[item.value]} ${
                  role === item.value ? 'is-active' : ''
                }`}
                onClick={() => setRole?.(item.value)}
                aria-pressed={role === item.value}
                aria-label={item.label}
              >
                {item.value === 'all'
                  ? <Tooltip as="span" className="fb__role-short" content={item.label}>{item.short}</Tooltip>
                  : <RoleChip role={item.value} />}
              </button>
            ))}
          </div>
        </div>

        {/* ORDINA */}
        <div className="fb__panel fb__panel--sort">
          <div className="fb__panel-head">
            <SlidersHorizontal size={13} aria-hidden="true" />
            <span>Ordina per</span>
          </div>
          <div className="fb__sort-row" role="group" aria-label="Ordinamento">
            {SORT_OPTIONS.map((item) => (
              <Tooltip
                as="button"
                key={item.value}
                type="button"
                className={`fb__sort-btn ${sort === item.value ? 'is-active' : ''}`}
                onClick={() => setSort?.(item.value)}
                aria-pressed={sort === item.value}
                content={item.hint || item.label}
              >
                {item.label}
              </Tooltip>
            ))}
          </div>
        </div>

      </div>

      {/* ── Squadre (full-width, collassabile) ── */}
      <div className="fb__panel fb__panel--teams">
        <div className="fb__teams-head">
          <div className="fb__panel-head">
            <Users size={13} aria-hidden="true" />
            <span>Squadra</span>
          </div>
          {hasMoreTeams && (
            <button
              type="button"
              className="fb__teams-toggle"
              onClick={() => setTeamsExpanded((v) => !v)}
              aria-expanded={teamsExpanded}
            >
              {teamsExpanded ? (
                <><ChevronUp size={13} /> Mostra meno</>
              ) : (
                <><ChevronDown size={13} /> Tutte ({teams.length})</>
              )}
            </button>
          )}
        </div>

        <div className="fb__teams-grid" role="group" aria-label="Filtro squadra">
          <button
            type="button"
            className={`fb__team-all ${team === 'all' ? 'is-active' : ''}`}
            onClick={() => setTeam?.('all')}
            aria-pressed={team === 'all'}
          >
            Tutte
          </button>

          {visibleTeams.map((item) => (
            <button
              key={item}
              type="button"
              className={`fb__team-btn ${team === item ? 'is-active' : ''}`}
              onClick={() => setTeam?.(item)}
              aria-pressed={team === item}
              aria-label={item}
            >
              <TeamBadge team={item} size={46} />
              <span className="fb__sr-only">{item}</span>
            </button>
          ))}
        </div>
      </div>

      </div>
    </section>
  );
}
